import { useSelector } from "react-redux";
import { tv } from "tailwind-variants";
import { SUB_STATUS } from "./constants";
import type { WorkflowStepInstance } from "@/types/WorkflowInstance";
import { selectSelectedStepInstanceId } from "../workflowInstanceSlice";

const styles = tv({
  slots: {
    stepSelector:
      "pointer-events-none text-left text-mono overflow-hidden text-ellipsis whitespace-nowrap",
  },
  variants: {
    status: {
      Completed: {
        stepSelector: "text-success-dark cursor-pointer pointer-events-auto",
      },
      InProgress: {
        stepSelector: "text-mono-darker cursor-pointer pointer-events-auto",
      },
      Pending: {
        stepSelector: "",
      },
      AwaitingUser: {
        stepSelector: "text-warning-dark cursor-pointer pointer-events-auto",
      },
      Failed: {},
      Cancelled: {},
    },
    isBeingViewed: {
      true: {
        stepSelector: "text-mono-darker",
      },
      false: {},
    },
  },
  compoundVariants: [
    {
      status: "InProgress",
      isBeingViewed: true,
      class: {
        stepSelector: "font-normal",
      },
    },
    {
      status: "AwaitingUser",
      isBeingViewed: true,
      class: {
        stepSelector: "text-warning-dark font-bold",
      },
    },
    {
      status: "Completed",
      isBeingViewed: true,
      class: {
        stepSelector: "text-success-dark font-bold",
      },
    },
  ],
});

const { stepSelector: stepSelectorStyle } = styles();

interface WorkflowSidebarSectionStepProps {
  step: WorkflowStepInstance;
  onSelect: (step: WorkflowStepInstance) => void;
}

export function WorkflowSidebarSectionStep({
  step,
  onSelect,
}: Readonly<WorkflowSidebarSectionStepProps>) {
  const selectedStepInstanceId = useSelector(selectSelectedStepInstanceId);

  const stepHasAttentionSubStatus =
    step.subStatus === SUB_STATUS.AWAITING_OUTPUTS_APPROVAL ||
    step.subStatus === SUB_STATUS.REQUIRES_ADDITIONAL_INFO;

  const stepStatus = stepHasAttentionSubStatus ? "AwaitingUser" : step.status;

  return (
    <button
      key={step.id}
      className={stepSelectorStyle({
        status: stepStatus,
        isBeingViewed: selectedStepInstanceId === step.id,
      })}
      data-testid={`workflow-step-selector-${step.id}`}
      onClick={() => onSelect(step)}
    >
      <span
        title={step.stepName}
        className="overflow-hidden text-ellipsis whitespace-nowrap"
        data-testid={`workflow-step-${step.id}`}
      >
        {step.stepName}
      </span>
    </button>
  );
}
